import React, { useRef, useEffect, useState, memo } from 'react';

import PropTypes from 'prop-types';
import { produce } from 'immer';
import { useSelector } from 'react-redux';
import { postSentimentAnalysis } from '@service/apis/analysis.api';

import { Summary } from '@components/summary';
import Chart from './solidgaugeChart';
import { number_format } from '@utils/widget';

import { defaultSentimentChartOption } from '../config';
// import SectionComment from '@components/CommentCenter/SectionComment';
// import CommentAnchor from '@components/directUser/CommentAnchor';
import CommentAnchor from 'src/ddvComment/directUser/CommentAnchor';
import SectionComment from 'src/ddvComment/SectionComment';

const titles = {
  overallRating: 'Overall Employee Rating of Company',
  careerOpportunitiesRating: 'Career Opportunities Rating',
  compensationAndBenefitsRating: 'Compensation and Benefits Rating',
  cultureAndValuesRating: 'Culture and Values Rating',
  seniorLeadershipRating: 'Senior Leadership Rating',
  workLifeBalanceRating: 'Work/Life Balance Rating',
};

export const reflowChart = (...charts) => {
  try {
    charts.forEach((chart) => {
      if (chart && chart.current && chart.current.chart.reflow) {
        chart.current.chart.reflow();
      }
    });
  } catch (error) {
    console.error(error);
  }
};

export const destroyCharts = (...charts) => {
  try {
    charts.forEach((chart) => {
      chart &&
        chart.current &&
        chart.current.destroy &&
        chart.current.destroy();
    });
  } catch (error) {
    console.error(error);
  }
};

function SentimentChart({
  data: {
    ratings,
    company,
    summary,
    ratings: { buyer: { numberOfRatings: buyerNumber = 0 } = {} } = {},
    ratings: { target: { numberOfRatings: targetNumber = 0 } = {} } = {},
  },
  refetch,
  currentSubmoduleName,
}) {
  const { buyerLabel, targetLabel } = useSelector(({ common }) => ({
    buyerLabel: common.projectCompanyInfo.buyerLabel,
    targetLabel: common.projectCompanyInfo.targetLabel,
  }));

  const chartRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ];
  const [options, setOptions] = useState([]);
  const summaryText = summary || '';

  useEffect(() => {
    if (!ratings) {
      return;
    }
    const { buyer, target } = ratings;
    const datas = [];
    buyer &&
      Object.keys(titles).forEach((item, idx) => {
        datas[idx] = {
          buyer: buyer[item] || 0,
          target: target[item] || 0,
          title: titles[item],
        };
      });
    const newOptions = [];
    datas.forEach((item, index) => {
      const options = produce(defaultSentimentChartOption, (draft) => {
        draft.title.text = item.title;
        if (index === 0) {
          draft.title.style = { color: '#FFFFFF', fontSize: '1rem' };
        }

        draft.series[1].name = buyerLabel;
        draft.series[0].name = targetLabel;

        // if (index === 0) {
        //   draft.series[0].data[0].color = colors[1][0];
        //   draft.series[1].data[0].color = colors[1][1];
        // }

        if (item.buyer && buyerNumber >= 50) {
          draft.series[1].data[0].y = item.buyer;
        } else {
          draft.series[1].data[0].y = 0;
          draft.series[1].data[0].dataLabels.format = `<span style="font-size:0.75rem!important;">${buyerLabel}  </span><span style="font-size:1.25rem!important;font-weight:bold!important;"> N/A</span>`;

          draft.series[1].data[0].color = '#303030';
        }
        if (item.target && targetNumber >= 50) {
          draft.series[0].data[0].y = item.target;
        } else {
          draft.series[0].data[0].y = 0;
          draft.series[0].data[0].dataLabels.format = `<span style="font-size:0.75rem!important;">${targetLabel}  </span><span style="font-size:1.25rem!important;font-weight:bold!important;">  N/A</span>`;

          draft.series[0].data[0].color = '#303030';
        }
      });
      newOptions.push(options);
    });

    setOptions(newOptions);
  }, [ratings, buyerLabel]);

  useEffect(() => {
    if (options.length !== 0) {
      reflowChart(...chartRefs);
    }
  }, [options]);

  return (
    <>
      <div className="section-comment-wrapper-in-sentiment">
        <SectionComment module={currentSubmoduleName} placement="bottomRight" />
      </div>
      <div className="sentiment-title">
        <div className="title">
          <div className="buyer-title">
            <span>{buyerLabel}</span>
            <div>
              {company.buyerName || '-'} <div className="buyer-dot" />
            </div>
          </div>
          <div className="target-title">
            <span>{targetLabel}</span>
            <div>
              {company.targetName || '-'} <div className="target-dot" />
            </div>
          </div>
        </div>
      </div>

      <div className="sentiment-summary">
        <Summary
          text={summaryText}
          onUpdate={refetch}
          className="sentiment-summary-box"
          saveSummary={postSentimentAnalysis}
        />

        <div className="sample">
          <div className="all-score">All scores are out of 5</div>
          <div className="all-score">
            {targetLabel} sample size:{' '}
            {targetNumber < 50 ? 'N/A' : number_format(targetNumber)}
            &nbsp; reviews
          </div>
          <div className="all-score">
            {buyerLabel} sample size:{' '}
            {buyerNumber < 50 ? 'N/A' : number_format(buyerNumber)}
            &nbsp; reviews
          </div>
        </div>
      </div>
      <div className="sentiment-chart">
        {options?.map((item, index) => (
          <div className="single-sentiment-chart" key={index}>
            <Chart ref={chartRefs[index]} options={options[index]} />
          </div>
        ))}
        <div className="footer">
          Source: Sentiment Analysis based on reviews and ratings from
          Glassdoor.com
        </div>
      </div>
    </>
  );
}

SentimentChart.propTypes = {
  data: PropTypes.object.isRequired,
};

export default CommentAnchor(memo(SentimentChart), {
  shouldScrollInToView: (anchorRecord, anchorId) =>
    anchorRecord?.moduleName === anchorId,
  getAnchorIdFromProp(props) {
    return props.currentSubmoduleName;
  },
});
